import { Schema } from "effect";
import { NonNegativeInt, PositiveInt, TrimmedNonEmptyString } from "./baseSchemas";

const PROJECT_SEARCH_ENTRIES_MAX_LIMIT = 200;
const PROJECT_WRITE_FILE_PATH_MAX_LENGTH = 512;
const PROJECT_LIST_DIRECTORY_PATH_MAX_LENGTH = 512;
const PROJECT_READ_FILE_MAX_BYTES = 5 * 1024 * 1024;

export const ProjectSearchEntriesInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  query: TrimmedNonEmptyString.check(Schema.isMaxLength(256)),
  limit: PositiveInt.check(Schema.isLessThanOrEqualTo(PROJECT_SEARCH_ENTRIES_MAX_LIMIT)),
});
export type ProjectSearchEntriesInput = typeof ProjectSearchEntriesInput.Type;

const ProjectEntryKind = Schema.Literals(["file", "directory"]);

export const ProjectEntry = Schema.Struct({
  path: TrimmedNonEmptyString,
  kind: ProjectEntryKind,
  parentPath: Schema.optional(TrimmedNonEmptyString),
  // True when the entry is excluded by the workspace ignore rules (gitignore).
  // Optional and absent for regular entries, so older clients keep working and
  // the common case stays off the wire. Ignored directories are surfaced as a
  // single entry: their contents are never indexed.
  ignored: Schema.optional(Schema.Boolean),
});
export type ProjectEntry = typeof ProjectEntry.Type;

export const ProjectSearchEntriesResult = Schema.Struct({
  entries: Schema.Array(ProjectEntry),
  truncated: Schema.Boolean,
});
export type ProjectSearchEntriesResult = typeof ProjectSearchEntriesResult.Type;

export const ProjectListDirectoryInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  // Workspace-relative directory path, no leading/trailing slash. Empty string
  // means the workspace root.
  dirPath: Schema.String.check(Schema.isMaxLength(PROJECT_LIST_DIRECTORY_PATH_MAX_LENGTH)),
});
export type ProjectListDirectoryInput = typeof ProjectListDirectoryInput.Type;

export const ProjectListDirectoryResult = Schema.Struct({
  entries: Schema.Array(ProjectEntry),
  // True when the workspace index itself hit its entry cap, so the listing may
  // be missing children.
  truncated: Schema.Boolean,
});
export type ProjectListDirectoryResult = typeof ProjectListDirectoryResult.Type;

export const ProjectWriteFileInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString.check(Schema.isMaxLength(PROJECT_WRITE_FILE_PATH_MAX_LENGTH)),
  contents: Schema.String,
  // Modification time (ms since epoch) the client based the edit on. When set,
  // the server rejects the write if the on-disk mtime differs — prevents
  // overwriting edits made externally (the agent, the user in another tool).
  baseMtimeMs: Schema.optional(NonNegativeInt),
});
export type ProjectWriteFileInput = typeof ProjectWriteFileInput.Type;

export const ProjectWriteFileConflict = Schema.Struct({
  kind: Schema.Literal("conflict"),
  currentMtimeMs: NonNegativeInt,
});
export type ProjectWriteFileConflict = typeof ProjectWriteFileConflict.Type;

export const ProjectWriteFileWritten = Schema.Struct({
  kind: Schema.Literal("written"),
  relativePath: TrimmedNonEmptyString,
  mtimeMs: NonNegativeInt,
});
export type ProjectWriteFileWritten = typeof ProjectWriteFileWritten.Type;

export const ProjectWriteFileResult = Schema.Union([
  ProjectWriteFileWritten,
  ProjectWriteFileConflict,
]);
export type ProjectWriteFileResult = typeof ProjectWriteFileResult.Type;

export const PROJECT_READ_FILE_MAX_SIZE_BYTES = PROJECT_READ_FILE_MAX_BYTES;

export const ProjectReadFileInput = Schema.Struct({
  cwd: TrimmedNonEmptyString,
  relativePath: TrimmedNonEmptyString.check(Schema.isMaxLength(PROJECT_WRITE_FILE_PATH_MAX_LENGTH)),
});
export type ProjectReadFileInput = typeof ProjectReadFileInput.Type;

export const ProjectReadFileResult = Schema.Union([
  Schema.Struct({
    kind: Schema.Literal("text"),
    relativePath: TrimmedNonEmptyString,
    contents: Schema.String,
    mtimeMs: NonNegativeInt,
    sizeBytes: NonNegativeInt,
    languageId: Schema.NullOr(TrimmedNonEmptyString),
  }),
  Schema.Struct({
    kind: Schema.Literal("missing"),
  }),
  Schema.Struct({
    kind: Schema.Literal("too-large"),
    sizeBytes: NonNegativeInt,
    maxBytes: NonNegativeInt,
  }),
  Schema.Struct({
    kind: Schema.Literal("binary"),
    sizeBytes: NonNegativeInt,
  }),
]);
export type ProjectReadFileResult = typeof ProjectReadFileResult.Type;
